import { AppBar, Box, Button, CssBaseline, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, Toolbar, Menu, MenuItem, ListItemIcon } from "@mui/material";
import { useState } from "react";
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageSelector from "./language-selector";
import { Link } from "react-router-dom";
import logo from "../../assets/logo-blanco.png";
import theme from "../../theme";
import { useTranslation } from "react-i18next";


const drawerWidth = 240;

export default function NavBar(props: { children: any, showLinks: boolean, showIcon?: boolean }) {
    const { t } = useTranslation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [servicesAnchorEl, setServicesAnchorEl] = useState<null | HTMLElement>(null);
    
    const navItems = [
        {
            title: t("nav.home"), route: './'
        },
        {
            title: t("nav.aboutUs"), route: 'aboutUs'
        },
        {
            title: t("nav.contactUs"), route: `contactUs`
        }
    ];

    const serviceItems = [
        { title: t("nav.h2aVisa"), route: '/h2a-visa' },
        { title: t("nav.h2bVisa"), route: '/h2b-visa' },
    ];

    const handleDrawerToggle = () => {
        setMobileOpen((prevState) => !prevState);
    };

    const handleServicesClick = (event: React.MouseEvent<HTMLElement>) => {
        setServicesAnchorEl(event.currentTarget);
    };

    const handleServicesClose = () => {
        setServicesAnchorEl(null);
    };

    const drawer = (
        <Box onClick={handleDrawerToggle}
            sx={{
                textAlign: 'center', margin: 0, padding: 0,
                backgroundColor: theme.palette.primary.main
            }}>
            <Link to={'/'}>
                <img alt="Laboria logo" style={{
                    marginTop: 8,
                    width: 200,
                    height: 90
                }} src={logo} />
            </Link>
            <Divider />
            {props.showLinks
                ?
                <List>
                    {navItems.map((item) => (
                        <ListItem key={item.title} disablePadding>
                            <ListItemButton sx={{ textAlign: 'center' }} href={`${item.route}`}>
                                <ListItemText primary={item.title} sx={{ color: 'white' }} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    
                    {/* Services Section */}
                    <ListItem disablePadding>
                        <ListItemButton sx={{ textAlign: 'center' }}>
                            <ListItemText primary={t("nav.services")} sx={{ color: 'white' }} />
                        </ListItemButton>
                    </ListItem>
                    
                    {serviceItems.map((item) => (
                        <ListItem key={item.title} disablePadding sx={{ pl: 4 }}>
                            <ListItemButton 
                                sx={{ textAlign: 'center' }} 
                                component={Link}
                                to={item.route}
                                onClick={handleDrawerToggle}
                            >
                                <ListItemText primary={item.title} sx={{ color: 'white', fontSize: '0.9rem' }} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
                : null}
        </Box >
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    return (
        <Box
            sx={{
                display: 'flex',
                margin: 0,
                padding: 0,
            }}>
            <CssBaseline />
            <AppBar component="nav" sx={{
                backgroundColor: 'primary',
                boxShadow: 'none',
            }}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'none', md: 'block' } }}>
                        <a href="/">
                            <img hidden={!props.showIcon} alt="Laboria logo" style={{
                                width: 130,
                                height: 65,
                                marginTop: 4

                            }} src={logo} />
                        </a>
                    </Box>


                    {props.showLinks
                        ? <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                            {navItems.map((item) => (
                                <Button key={item.title} href={`${item.route}`} sx={{
                                    color: 'white'
                                }}>
                                    {item.title}
                                </Button>
                            ))}
                            
                            {/* Services Dropdown */}
                            <Button
                                onClick={handleServicesClick}
                                sx={{
                                    color: 'white'
                                }}
                                endIcon={<KeyboardArrowDownIcon />}
                            >
                                {t("nav.services")}
                            </Button>
                            
                            <Menu
                                anchorEl={servicesAnchorEl}
                                open={Boolean(servicesAnchorEl)}
                                onClose={handleServicesClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                sx={{
                                    '& .MuiPaper-root': {
                                        backgroundColor: theme.palette.primary.main,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                        borderRadius: '8px',
                                        mt: 1,
                                        minWidth: 200,
                                        maxWidth: 250
                                    }
                                }}
                            >
                                {serviceItems.map((item) => (
                                    <MenuItem 
                                        key={item.title} 
                                        onClick={handleServicesClose}
                                        component={Link}
                                        to={item.route}
                                        sx={{
                                            padding: '12px 20px',
                                            fontSize: '0.95rem',
                                            fontWeight: 500,
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                                color: 'white'
                                            },
                                            transition: 'all 0.2s ease',
                                            borderBottom: `1px solid ${theme.palette.primary.dark}`,
                                            '&:last-child': {
                                                borderBottom: 'none'
                                            }
                                        }}
                                    >
                                        {item.title}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                        : null}
                    <LanguageSelector />
                </Toolbar>
            </AppBar>
            <nav>
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { sm: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: theme.palette.primary.main },
                    }}
                >
                    {drawer}
                </Drawer>
            </nav>
            <Box component="main" sx={{ width: '100%', margin: 0, padding: 0 }}>
                {props.children}
            </Box>
        </Box>
    );

}